import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { ReactNode, SyntheticEvent, TableHTMLAttributes } from 'react'
import { clsx } from 'clsx'
import { TableAccordionHead } from './table-accordion/TableAccordionHead'
import { TableNavigationHead } from './table-navigation/TableNavigationHead'
import { TableAccordionContentRow } from './table-accordion/TableAccordionContent'
import { TableContext } from './TableContext'
import { TableTrContext } from './TableTrContext'

export type TableTrClickInfo = {
  trElement: HTMLTableRowElement | null
}

export type TableTrProps = {
  /**
   * Override the automatic variant of the current row. The next row one will continue with the opposite.
   * Default: `undefined`
   */
  variant?: 'even' | 'odd'

  /**
   * If set to `true`, the inherited header text will not wrap to new lines.
   * Default: `false`
   */
  noWrap?: boolean

  /**
   * Vertical alignment of all cell content in the row.
   * Default: `undefined`
   */
  verticalAlign?: 'top' | 'middle' | 'bottom'

  /**
   * If set to `true`, all `<Td>` and `<Th>` cells in the row receive a highlighted background.
   * Default: `false`
   */
  highlight?: boolean

  /**
   * Use `true` to render the `<Tr>` initially as expanded.
   * Default: `false`
   */
  expanded?: boolean

  /**
   * Use `true` to disable the `<Tr>` to be accessible as an interactive element.
   * Default: `false`
   */
  disabled?: boolean

  /**
   * Use `true` to disable the expand/collapse animation.
   * Default: `false`
   */
  noAnimation?: boolean

  /**
   * When `true`, keeps the accordion content in the DOM when closed. Defaults to `false`.
   * Default: `false`
   */
  keepInDOM?: boolean

  /**
   * Will emit when user clicks/expands or on keydown space/enter (in `mode="accordion"` and `mode="navigation"`) in the table row. The second argument is an object with `trElement` (the `HTMLTableRowElement`).
   * Default: `undefined`
   */
  onClick?: (event: SyntheticEvent, info: TableTrClickInfo) => void

  /**
   * Will emit when table row is expanded. Returns an object with the table row as the target: `{ target }`.
   * Default: `undefined`
   */
  onOpen?: ({ target }: { target: HTMLTableRowElement }) => void

  /**
   * Will emit when table row is closed (after it was open). Returns an object with the table row as the target: `{ target }`.
   * Default: `undefined`
   */
  onClose?: ({ target }: { target: HTMLTableRowElement }) => void

  /**
   * The content of the component.
   */
  children: ReactNode
}

export default function Tr(
  componentProps: TableTrProps &
    Omit<TableHTMLAttributes<HTMLTableRowElement>, 'onClick'>
) {
  const {
    variant,
    noWrap,
    verticalAlign,
    highlight,
    className: _className,
    ...restProps
  } = componentProps

  const { currentVariant, isLast, count } = useHandleTrVariant({
    variant,
  })

  const className = clsx(
    'dnb-table__tr',
    currentVariant && `dnb-table__tr--${currentVariant}`,
    isLast && 'dnb-table__tr--last',
    noWrap && 'dnb-table--no-wrap',
    verticalAlign && `dnb-table__tr--vertical-align-${verticalAlign}`,
    _className
  )

  const tableContext = useContext(TableContext)
  const trContext = highlight ? { highlight } : null

  if (tableContext?.allProps?.mode == 'accordion') {
    return (
      <TableTrContext value={trContext}>
        <TableAccordionHead
          count={count}
          className={className}
          {...restProps}
        />
      </TableTrContext>
    )
  }
  if (tableContext?.allProps?.mode === 'navigation') {
    return (
      <TableTrContext value={trContext}>
        <TableNavigationHead className={className} {...restProps} />
      </TableTrContext>
    )
  }

  const {
    expanded,
    disabled,
    noAnimation,
    keepInDOM,
    onClick,
    onOpen,
    onClose,
    ...trProps
  } = restProps

  return (
    <TableTrContext value={trContext}>
      <tr role="row" className={className} {...trProps} />
    </TableTrContext>
  )
}

function useHandleTrVariant({ variant }) {
  const tableContext = useContext(TableContext)

  /**
   * Handle odd/even
   */
  const countRef = tableContext?.trCountRef.current
  const lastRenderAliasRef = useRef(null)
  const hasIncrementedRef = useRef(false)

  const increment = useCallback(() => {
    if (typeof countRef === 'undefined') {
      return 0
    }
    if (
      !variant ||
      (variant === 'even' && countRef.count % 2 === 1) ||
      (variant === 'odd' && countRef.count % 2 === 0)
    ) {
      countRef.count++
    }

    return countRef.count
  }, [countRef, variant])

  const [count, setCount] = useState(() => {
    // Guard against StrictMode double-invocation of useState initializer.
    // Refs persist across StrictMode's double-calls, so the second call
    // returns the current count without re-incrementing.
    if (hasIncrementedRef.current) {
      return countRef?.count ?? 0
    }

    hasIncrementedRef.current = true
    return increment()
  })

  useEffect(() => {
    if (lastRenderAliasRef.current === null) {
      // Initial mount - useState already handled the count
      lastRenderAliasRef.current = tableContext?.rerenderAlias
      return // stop here
    }

    if (lastRenderAliasRef.current !== tableContext?.rerenderAlias) {
      lastRenderAliasRef.current = tableContext?.rerenderAlias
      hasIncrementedRef.current = false
    }

    if (!hasIncrementedRef.current) {
      hasIncrementedRef.current = true
      setCount(increment())
    }
  }, [tableContext?.rerenderAlias, increment])

  /**
   * Find out the current odd/even when "accordionContent" is used.
   * Because we have now an additional "tr" element.
   * Then the CSS can't figure out the correct nth element (nth-of-type)
   * and we need to set it manually (nth-child and nth-of-type do not respect classes, so we can't ignore this one).
   */
  let currentVariant = variant
  if (!currentVariant) {
    currentVariant = count % 2 ? 'odd' : 'even'
  }
  const isLast =
    tableContext?.totalCount > 0
      ? tableContext.totalCount === count
      : typeof countRef !== 'undefined' && countRef.count === count
  return {
    currentVariant,
    isLast,
    count,
  }
}

/**
 * Handle odd/even on re-render and StrictMode
 */
export function useHandleOddEven({ children }) {
  // Create this ref in order to "auto" set even/odd class in tr elements
  const trCountRef = useRef({ count: 0 })

  // Stable total count, updated after all Tr effects have settled.
  // Reading countRef.count during render is unreliable because each Tr
  // mutates it via increment(), so intermediate renders see partial values.
  const [totalCount, setTotalCount] = useState(0)

  // When the alias changes, all tr's will rerender and get a new even/odd color
  // This is useful, when one tr gets removed
  const [rerenderAlias, setRerenderAlias] = useState({})

  const forceRerender = useCallback(() => {
    trCountRef.current.count = 0
    setRerenderAlias({})
  }, [])

  const isMounted = useRef(false)
  useEffect(() => {
    if (isMounted.current) {
      forceRerender()
    }
    isMounted.current = true
  }, [children, forceRerender])

  // Runs after all child (Tr) effects, so trCountRef.current.count is final.
  // The dependency array is required: without it the effect runs after every
  // commit and would loop indefinitely when the Table lives inside content
  // that re-renders on its own (e.g. an infinity-scrolled Pagination), since
  // each re-render mutates trCountRef.current.count and setTotalCount would
  // trigger yet another render.
  useEffect(() => {
    setTotalCount(trCountRef.current.count)
  }, [children, rerenderAlias])

  return { trCountRef, rerenderAlias, totalCount, setRerenderAlias }
}

Tr.AccordionContent = TableAccordionContentRow
